import { useState, useEffect } from 'react';
import { ApolloError, useQuery } from '@apollo/client';
import { NavigationElementPosition, useBreakpoint } from '@landr/maestro';
import { HeroBannerContentType, HeroBanner } from 'types/generated/graphql';
import { log } from 'utils/log';
import { EventIds } from '../../../constants';
import { HeroBannersQuery } from '../gql';
import getSlides from '../helpers/getSlides';

const getPlayerIds = (heroBanners: HeroBanner[]) => {
    const playerIds = heroBanners
        ?.map((banner: HeroBanner): string | undefined => {
            const isLabel = banner.contentType === HeroBannerContentType.Label;
            const isCustom =
                banner.contentType === HeroBannerContentType.Custom;

            const firstPacks =
                (banner.labelPacks && banner.labelPacks[0]) ?? undefined;

            let demoGuid: string | undefined = isLabel
                ? firstPacks?.guid
                : banner.entity?.guid;

            return isCustom ? banner.guid : demoGuid;
        })
        ?.filter((guid: string | undefined) => guid);

    return playerIds;
};

const useHeroCarousel = ({
    get,
    isAutoPlayAuthorized,
    enableAutoPlay,
    stopActivePlayer,
    getActivePlayerId,
}: {
    get: (flag: string) => boolean;
    isAutoPlayAuthorized: boolean;
    enableAutoPlay: () => void;
    stopActivePlayer: () => void;
    getActivePlayerId: () => string | undefined;
}) => {
    const isMobile = useBreakpoint('md');
    const [playerIds, setPlayerIds] = useState<(string | undefined)[]>([]);

    const handleNavigate = () => {
        const activePlayerId = getActivePlayerId();

        const isActivePlayerFromHeroBanner = !!playerIds?.find(
            (id) => id === activePlayerId,
        );

        if (isActivePlayerFromHeroBanner) {
            stopActivePlayer();
        }

        if (!isAutoPlayAuthorized) {
            enableAutoPlay();
        }
    };

    const { data, loading } = useQuery(HeroBannersQuery, {
        onError: (error: ApolloError) => {
            log.error(
                'Failed to get hero banners',
                EventIds.FailedGetHeroBanner,
                error,
            );
        },
    });

    useEffect(() => {
        const playerIds = getPlayerIds(data?.heroBanners);
        setPlayerIds(playerIds);
    }, [data]);

    const slides = getSlides({
        heroBanners: data?.heroBanners,
    });

    const dotPosition =
        slides.length > 1 ? ('inside' as NavigationElementPosition) : undefined;

    const isAutoNavigationAllowed =
        !isMobile && isAutoPlayAuthorized && (!loading || slides.length !== 0);
    const autoNavigationDelay = isAutoNavigationAllowed ? 5000 : undefined;

    return {
        slides,
        dotPosition,
        loading,
        autoNavigationDelay,
        handleNavigate,
    };
};

export default useHeroCarousel;
